import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { CircleAlert, Download, LoaderCircle, Trash2 } from 'lucide-react';
import { useBlobUrl } from '@/hooks/use-blob-url';
import { useErrorText } from '@/lib/error-text';
import { canPreview, formatSize, iconFor, isImage } from '@/lib/files';
import { saveBlob } from '@/lib/save-blob';
import { cn } from '@/lib/utils';
import { Button, buttonVariants } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

export interface GalleryFile {
  id: string;
  filename: string;
  contentType: string;
  size: number;
}

export function FileGallery<T extends GalleryFile>({
  files,
  load,
  disabled,
  empty,
  onRemove,
  className,
}: {
  files: T[];
  load: (file: T) => Promise<Blob>;
  disabled?: boolean;
  empty?: ReactNode;
  onRemove?: (file: T) => void;
  className?: string;
}) {
  const { t } = useTranslation();
  const errorText = useErrorText();
  const [preview, setPreview] = useState<{ file: T; url?: string } | null>(null);
  const [downloading, setDownloading] = useState<string | null>(null);
  const [error, setError] = useState<unknown>();

  if (files.length === 0) {
    return empty ? <p className="py-2 text-sm text-muted-foreground">{empty}</p> : null;
  }

  async function open(file: T, thumbnail?: string) {
    if (canPreview(file.contentType)) {
      setPreview({ file, url: thumbnail });
      return;
    }

    setDownloading(file.id);
    setError(undefined);
    try {
      saveBlob(await load(file), file.filename);
    } catch (cause) {
      setError(cause);
    } finally {
      setDownloading(null);
    }
  }

  return (
    <div className={cn('flex flex-col gap-2', className)}>
      <ul className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
        {files.map((file) => (
          <Tile
            key={file.id}
            file={file}
            load={load}
            disabled={disabled}
            busy={downloading === file.id}
            removeLabel={t('files.remove', { filename: file.filename })}
            onOpen={(thumbnail) => void open(file, thumbnail)}
            onRemove={onRemove && (() => onRemove(file))}
          />
        ))}
      </ul>

      {Boolean(error) && (
        <p className="flex items-center gap-1.5 text-sm text-destructive">
          <CircleAlert className="size-4 shrink-0" />
          {errorText(error)}
        </p>
      )}

      {preview &&
        (preview.url ? (
          <FilePreviewDialog
            filename={preview.file.filename}
            contentType={preview.file.contentType}
            url={preview.url}
            onClose={() => setPreview(null)}
          />
        ) : (
          <LoadingPreviewDialog
            key={preview.file.id}
            file={preview.file}
            load={load}
            onClose={() => setPreview(null)}
          />
        ))}
    </div>
  );
}

function Tile<T extends GalleryFile>({
  file,
  load,
  disabled,
  busy,
  removeLabel,
  onOpen,
  onRemove,
}: {
  file: T;
  load: (file: T) => Promise<Blob>;
  disabled?: boolean;
  busy?: boolean;
  removeLabel: string;
  onOpen: (thumbnail?: string) => void;
  onRemove?: () => void;
}) {
  const { t } = useTranslation();
  const Icon = iconFor(file.filename, file.contentType);
  const thumbnail = useThumbnail(file, load);

  return (
    <li className="group relative">
      <button
        type="button"
        className="flex w-full flex-col overflow-hidden rounded-lg border text-left transition-colors hover:bg-muted/50 disabled:opacity-50"
        disabled={disabled || busy}
        aria-label={t('files.open', { filename: file.filename })}
        onClick={() => onOpen(thumbnail.url)}
      >
        <span className="flex aspect-4/3 w-full items-center justify-center bg-muted/50">
          {busy ? (
            <LoaderCircle className="size-5 animate-spin text-muted-foreground" />
          ) : thumbnail.url ? (
            <img
              src={thumbnail.url}
              alt={file.filename}
              loading="lazy"
              className="size-full object-cover"
            />
          ) : (
            <Icon className="size-8 text-muted-foreground" strokeWidth={1.5} />
          )}
        </span>

        <span className="flex min-w-0 flex-col gap-0.5 p-2">
          <span className="truncate text-sm">{file.filename}</span>
          <span className="text-xs text-muted-foreground">{formatSize(file.size)}</span>
        </span>
      </button>

      {onRemove && (
        <Button
          type="button"
          variant="secondary"
          size="icon-sm"
          className="absolute top-1.5 right-1.5 opacity-0 transition-opacity group-hover:opacity-100 focus-visible:opacity-100"
          aria-label={removeLabel}
          disabled={disabled || busy}
          onClick={onRemove}
        >
          <Trash2 />
        </Button>
      )}
    </li>
  );
}

function useThumbnail<T extends GalleryFile>(file: T, load: (file: T) => Promise<Blob>) {
  return useBlobUrl(isImage(file.contentType) ? file.id : '', () => load(file));
}

function LoadingPreviewDialog<T extends GalleryFile>({
  file,
  load,
  onClose,
}: {
  file: T;
  load: (file: T) => Promise<Blob>;
  onClose: () => void;
}) {
  const { url, error } = useBlobUrl(file.id, () => load(file));

  return (
    <FilePreviewDialog
      filename={file.filename}
      contentType={file.contentType}
      url={url}
      error={error}
      onClose={onClose}
    />
  );
}

export function FilePreviewDialog({
  filename,
  contentType,
  url,
  error,
  onClose,
}: {
  filename: string;
  contentType: string;
  url?: string;
  error?: unknown;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const errorText = useErrorText();

  return (
    <Dialog open onOpenChange={(next) => !next && onClose()}>
      <DialogContent className="sm:max-w-3xl">
        <DialogHeader>
          <DialogTitle className="truncate pr-8">{filename}</DialogTitle>
        </DialogHeader>

        <div className="flex h-[65vh] items-center justify-center overflow-auto rounded-md bg-muted/50">
          {error ? (
            <p className="flex items-center gap-1.5 text-sm text-destructive">
              <CircleAlert className="size-4 shrink-0" />
              {errorText(error)}
            </p>
          ) : !url ? (
            <LoaderCircle className="size-5 animate-spin text-muted-foreground" />
          ) : contentType === 'application/pdf' ? (
            <iframe src={url} title={filename} className="size-full rounded-md" />
          ) : (
            <img src={url} alt={filename} className="max-h-full max-w-full object-contain" />
          )}
        </div>

        <DialogFooter>
          <Button type="button" variant="outline" onClick={onClose}>
            {t('common.close')}
          </Button>
          {url && (
            <a href={url} download={filename} className={buttonVariants()}>
              <Download />
              {t('common.download')}
            </a>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
